// Add SOW dialog. Owns local form state + validation; delegates rendering to
// SowFormFields. Mirrors the meeting form modal pattern. The Edit flow lives
// inline (SowInlineExpand), not in this modal — `mode === "edit"` is supported
// here for symmetry but the primary path is create.

import { useState } from "react";
import Modal from "../../../common/Modal";
import Button from "../../../common/Button";
import Alert from "../../../common/Alert";
import SowFormFields from "./SowFormFields";
import useSeededFormState from "../../../../hooks/useSeededFormState";
import {
  buildEmptySowFormState,
  parseOriginalValue,
  stateFromSow,
  toCreateSowInput,
  toUpdateSowInput,
  type SowFieldErrors,
  type SowFormState,
} from "./sowFormState";
import { nextTrackingNumber } from "./nextTrackingNumber";
import { isValidIsoDate } from "../shared/isoDateValidation";
import type {
  CreateSowInput,
  Sow,
  UpdateSowInput,
} from "../../../../types";

export type SowFormMode = "create" | "edit";

interface SowFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: SowFormMode;
  contractId: string;
  source?: Sow;
  gstApplies: boolean | undefined;
  /** Existing SOWs on the contract — used to compute the canonical next
   *  number client-side so the user sees the allocated form before they
   *  hit Save. The backend still allocates atomically when
   *  numberOverrideMode is false, so this is a display hint only. */
  existingSows?: readonly Sow[];
  /** Number format from the contract's `sowNumberFormat` setting. When
   *  `"nn"` the seeded preview is `01`, `02`, ...; when `"sow-yyyy-nnn"`
   *  it is `SOW-2026-001`, etc. Defaults to `"nn"` to mirror the
   *  per-contract default. */
  numberFormat?: "nn" | "sow-yyyy-nnn";
  onCreate?: (input: CreateSowInput) => Promise<unknown>;
  onUpdate?: (input: UpdateSowInput) => Promise<unknown>;
  validateUniqueNumber?: (next: string) => Promise<string | null>;
  isSubmitting?: boolean;
}

function validate(
  state: SowFormState,
  numberFormat: "nn" | "sow-yyyy-nnn",
): SowFieldErrors {
  const errors: SowFieldErrors = {};
  if (!state.name.trim()) errors.name = "Name is required";
  if (!isValidIsoDate(state.startDate)) {
    errors.startDate = "Enter a valid start date";
  }
  if (!isValidIsoDate(state.endDate)) {
    errors.endDate = "Enter a valid end date";
  }
  if (
    !errors.startDate &&
    !errors.endDate &&
    state.endDate < state.startDate
  ) {
    errors.endDate = "End date must be on or after start date";
  }
  const v = parseOriginalValue(state.originalValue);
  if (v <= 0) {
    errors.originalValue = "Enter a value greater than $0";
  }
  // Format check on a typed override — shape depends on the contract's
  // chosen format. Empty value means the user accepted the auto-generated
  // default; skip the check.
  if (
    state.numberOverrideMode &&
    state.number.trim().length > 0
  ) {
    const trimmed = state.number.trim();
    if (numberFormat === "nn") {
      if (!/^\d+$/.test(trimmed)) {
        errors.number = "Number must be digits only (e.g. 01)";
      }
    } else if (!/^SOW-\d{4}-\d{3}$/.test(trimmed)) {
      errors.number = "Number must match SOW-YYYY-NNN";
    }
  }
  return errors;
}

function hasErrors(errors: SowFieldErrors): boolean {
  return Object.values(errors).some((v) => v !== undefined);
}

const modeTitle: Record<SowFormMode, string> = {
  create: "Add SOW",
  edit: "Edit SOW",
};

export default function SowFormModal({
  isOpen,
  onClose,
  mode,
  contractId,
  source,
  gstApplies,
  existingSows = [],
  numberFormat = "nn",
  onCreate,
  onUpdate,
  validateUniqueNumber,
  isSubmitting,
}: SowFormModalProps) {
  const [errors, setErrors] = useState<SowFieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Re-seed whenever the modal opens or its target changes. The seedKey
  // encodes mode + source id so that:
  //   - Re-opens for a different SOW always seed fresh data.
  //   - A freshly-derived existingSows array from the parent does not re-seed
  //     what the user has already typed (existingSows is only read at seed
  //     time, not tracked as a dependency).
  // The modal is eager-mounted (only isOpen toggles) — useSeededFormState
  // resets its guard on close, so reopening with the same key (e.g.
  // create → create) re-seeds instead of showing the previous values.
  const [state, setState] = useSeededFormState<SowFormState>(
    isOpen,
    `${mode}:${source?.id ?? "new"}`,
    () => {
      if (mode === "edit" && source) return stateFromSow(source);
      const seeded = buildEmptySowFormState();
      seeded.number = nextTrackingNumber(
        "SOW",
        existingSows,
        new Date(),
        numberFormat,
      );
      return seeded;
    },
    () => {
      setErrors({});
      setSubmitError(null);
    },
  );

  const handleChange = (patch: Partial<SowFormState>) => {
    setState((prev) => ({ ...prev, ...patch }));
    const keys = Object.keys(patch);
    setErrors((prev) => {
      const next = { ...prev };
      for (const k of keys) {
        delete next[k as keyof SowFieldErrors];
      }
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = validate(state, numberFormat);
    if (hasErrors(v)) {
      setErrors(v);
      return;
    }
    setSubmitError(null);
    try {
      if (mode === "edit" && source && onUpdate) {
        await onUpdate(toUpdateSowInput(state, contractId, source.version));
      } else if (mode === "create" && onCreate) {
        await onCreate(toCreateSowInput(state, contractId));
      }
      onClose();
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "Failed to save SOW.",
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modeTitle[mode]}
      maxWidth="sm:max-w-2xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <SowFormFields
          value={state}
          onChange={handleChange}
          errors={errors}
          idPrefix={`sow-${mode}`}
          mode={mode}
          gstApplies={gstApplies}
          validateUniqueNumber={validateUniqueNumber}
          disabled={isSubmitting}
        />
        {submitError && <Alert variant="error">{submitError}</Alert>}
        <div className="flex justify-end gap-3 pt-2">
          <Button
            variant="secondary"
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
            {mode === "edit" ? "Save changes" : "Create SOW"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
